<template>
	<div v-loading="loading" class="mall-payment-page" element-loading-text="正在核对结算商品...">
		<div class="payment-container">
			<div class="breadcrumb-wrap">
				<el-breadcrumb separator-class="el-icon-arrow-right">
					<el-breadcrumb-item :to="{ path: '/mallStore/index' }">服务商城</el-breadcrumb-item>
					<el-breadcrumb-item>确认订单</el-breadcrumb-item>
					<el-breadcrumb-item>订单支付</el-breadcrumb-item>
				</el-breadcrumb>
			</div>

			<section v-if="paymentSucceeded" class="payment-success-card">
				<div class="success-icon"><i class="el-icon-check"></i></div>
				<h1>支付成功</h1>
				<p class="success-order-number">
					订单编号：{{ paymentResult.orderCode }}
					<span v-if="paymentResult.orderCount > 1">（共 {{ paymentResult.orderCount }} 笔订单）</span>
				</p>
				<div v-if="paymentOrderCodes.length > 1" class="success-order-list">
					<span v-for="item in paymentOrderCodes" :key="item.orderId">{{ item.shopName }}：{{ item.orderCode }}</span>
				</div>
				<div class="success-amount">
					<small>¥</small>
					{{ formatAmount(paymentResult.payableAmount) }}
				</div>

				<div class="success-summary">
					<div>
						<span>支付方式</span>
						<strong>{{ paymentMethodLabel }}</strong>
					</div>
					<div>
						<span>商品总额</span>
						<strong>¥ {{ formatAmount(paymentResult.goodsAmount) }}</strong>
					</div>
					<div>
						<span>下一处理环节</span>
						<strong>{{ paymentResult.nextAuditStage || '管理员审核' }}</strong>
					</div>
					<div class="success-paid-row">
						<span>实付金额</span>
						<strong>¥ {{ formatAmount(paymentResult.payableAmount) }}</strong>
					</div>
				</div>

				<p class="success-tip">{{ paymentSuccessTip }}</p>
				<div class="success-actions">
					<el-button @click="$router.push('/user/shoppmanag/shoppOrder')">查看订单</el-button>
					<el-button @click="$router.push('/user/shoppmanag/myCoupon')">查看券包</el-button>
					<el-button type="primary" @click="$router.push('/mallStore/index')">继续购物</el-button>
				</div>
			</section>

			<template v-else>
				<header class="payment-heading">
					<div>
						<span class="heading-icon"><i class="el-icon-wallet"></i></span>
						<div>
							<h1>确认订单并支付</h1>
							<p>请核对商品信息，选择优惠券和支付方式后完成付款</p>
						</div>
					</div>
					<span class="secure-tip">
						<i class="el-icon-lock"></i>
						支付环境安全保护中
					</span>
				</header>

				<div class="payment-layout">
					<div class="order-column">
						<section class="order-card">
							<div class="card-heading">
								<div>
									<span class="heading-mark"></span>
									<h2>订单信息</h2>
								</div>
								<div class="order-number-wrap">
									<span class="order-number">支付单号：{{ orderInfo.orderNo }}</span>
									<span :class="['payment-countdown', { expired: paymentExpired }]">
										<i class="el-icon-time"></i>
										{{ paymentExpired ? paymentStatusMessage : `支付剩余 ${countdownText}` }}
									</span>
								</div>
							</div>

							<div class="goods-table-heading">
								<span>商品信息</span>
								<span>单价</span>
								<span>数量</span>
								<span>小计</span>
							</div>

							<div v-for="merchant in orderInfo.merchantGroups" :key="merchant.merchantId" class="merchant-group">
								<div class="merchant-heading">
									<span class="merchant-logo"><i class="el-icon-office-building"></i></span>
									<strong>{{ merchant.merchantName }}</strong>
									<span v-if="merchant.selfOperated" class="self-tag">平台自营</span>
									<span class="merchant-service">
										<i class="el-icon-service"></i>
										联系客服
									</span>
								</div>

								<div v-for="goods in merchant.goodsList" :key="goods.goodsId" class="order-goods-row">
									<div class="goods-info">
										<div class="goods-image">
											<img :src="goods.image" :alt="goods.title" @error="handleImageError" />
										</div>
										<div class="goods-copy">
											<h3>{{ goods.title }}</h3>
											<p>{{ goods.introduction }}</p>
											<span>{{ goods.category }}</span>
										</div>
									</div>
									<div class="unit-price">
										<small>¥</small>
										{{ formatAmount(goods.unitPrice) }}
									</div>
									<div class="goods-quantity">× {{ goods.quantity }}</div>
									<div class="subtotal">
										<small>¥</small>
										{{ formatAmount(goods.unitPrice * goods.quantity) }}
									</div>
								</div>
							</div>
						</section>

						<section class="remark-card">
							<div class="remark-heading">
								<div>
									<h2>订单备注</h2>
									<p>选填，如有特殊需求可在此说明</p>
								</div>
								<span>{{ orderRemark.length }}/200</span>
							</div>
							<el-input
								v-model="orderRemark"
								type="textarea"
								:rows="4"
								maxlength="200"
								placeholder="请输入订单备注，建议先与商家确认相关需求"
							></el-input>
						</section>
					</div>

					<aside class="settlement-column">
						<section class="settlement-card">
							<div class="settlement-heading">
								<h2>订单结算</h2>
								<span>共 {{ goodsQuantity }} 件商品</span>
							</div>

							<div class="amount-list">
								<div>
									<span>商品总额</span>
									<strong>¥ {{ formatAmount(goodsTotal) }}</strong>
								</div>
								<div class="discount-row">
									<span>优惠减免</span>
									<strong>- ¥ {{ formatAmount(discountAmount) }}</strong>
								</div>
							</div>

							<div class="payable-amount">
								<span>应付金额</span>
								<div>
									<small>¥</small>
									<strong>{{ formatAmount(payableAmount) }}</strong>
								</div>
							</div>

							<div v-loading="couponUpdating" class="settlement-section coupon-section">
								<div class="section-title">
									<div>
										<i class="el-icon-tickets"></i>
										选择优惠券
									</div>
									<span>{{ usableCouponCount }} 张可用</span>
								</div>
								<div v-if="couponEnabled && couponList.length" class="coupon-select-wrap">
									<el-select
										v-model="selectedCouponId"
										class="coupon-select"
										size="small"
										placeholder="请选择优惠券"
										popper-class="checkout-coupon-popper"
										:disabled="couponUpdating || paymentExpired"
										@change="handleCouponChange"
									>
										<el-option value="" label="不使用优惠券">
											<div class="coupon-dropdown-row coupon-dropdown-none">
												<span class="coupon-dropdown-value">不使用</span>
												<div class="coupon-dropdown-copy">
													<strong>不使用优惠券</strong>
													<small>按商品原价结算</small>
												</div>
											</div>
										</el-option>
										<el-option
											v-for="coupon in couponList"
											:key="coupon.id"
											:value="coupon.id"
											:label="couponSelectLabel(coupon)"
											:disabled="!coupon.usable"
										>
											<div class="coupon-dropdown-row">
												<span class="coupon-dropdown-value">{{ couponValueText(coupon) }}</span>
												<div class="coupon-dropdown-copy">
													<strong>{{ coupon.name }}</strong>
													<small>{{ coupon.description }}</small>
												</div>
												<span v-if="coupon.usable" class="coupon-dropdown-saving">-¥{{ formatAmount(coupon.amount) }}</span>
												<span v-else class="coupon-dropdown-status">不可用</span>
											</div>
										</el-option>
									</el-select>
									<div v-if="selectedCoupon" class="coupon-selected-summary">
										<span>
											<i class="el-icon-circle-check"></i>
											{{ selectedCoupon.description }}
										</span>
										<strong>- ¥ {{ formatAmount(discountAmount) }}</strong>
									</div>
								</div>
								<div v-else class="coupon-empty">
									<i class="el-icon-tickets"></i>
									<span>暂无可用于当前订单的优惠券</span>
								</div>
							</div>

							<div class="settlement-section payment-section">
								<div class="section-title">
									<div>
										<i class="el-icon-bank-card"></i>
										选择支付方式
									</div>
								</div>
								<div class="payment-method-list">
									<label
										v-for="method in paymentMethods"
										:key="method.value"
										:class="['payment-method', { active: payMethod === method.value }]"
									>
										<input v-model="payMethod" type="radio" :value="method.value" />
										<img v-if="method.image" :src="method.image" :alt="method.label" />
										<span v-else :class="['payment-icon', method.iconClass]">
											<i :class="method.icon || 'el-icon-bank-card'"></i>
										</span>
										<div>
											<strong>{{ method.label }}</strong>
											<span>{{ method.description }}</span>
										</div>
										<i class="method-check el-icon-check"></i>
									</label>
								</div>
							</div>

							<el-alert v-if="paymentExpired" :title="paymentStatusMessage" type="warning" :closable="false" show-icon></el-alert>
							<el-button type="primary" class="pay-button" :loading="paying" :disabled="paymentExpired" @click="handlePay">
								{{ paymentExpired ? '订单已关闭' : `立即支付 ¥ ${formatAmount(payableAmount)}` }}
							</el-button>
							<p class="agreement-tip">
								<i class="el-icon-circle-check"></i>
								点击支付即表示您已确认订单信息并同意平台交易规则
							</p>
						</section>
					</aside>
				</div>
			</template>
		</div>

		<el-dialog
			title="招行聚合码支付"
			:visible.sync="qrDialogVisible"
			width="430px"
			custom-class="cmb-pay-dialog"
			:close-on-click-modal="false"
			append-to-body
			@closed="handleQrDialogClosed"
		>
			<div class="cmb-qrcode-content">
				<p class="cmb-qrcode-order">支付单号：{{ cmbPaymentResult.payOrderNo }}</p>
				<img
					v-if="cmbPaymentResult.qrCodeImage"
					class="cmb-qrcode-image"
					:src="cmbPaymentResult.qrCodeImage"
					alt="招行聚合收款码"
				/>
				<p class="cmb-qrcode-amount">支付金额：¥ {{ formatAmount(cmbPaymentResult.payableAmount) }}</p>
				<p class="cmb-qrcode-tip">请使用支付宝或微信扫描二维码完成支付</p>
				<p v-if="cmbPaymentResult.qrExpireTime" class="cmb-qrcode-expire">二维码有效期至：{{ cmbPaymentResult.qrExpireTime }}</p>
			</div>
			<span slot="footer">
				<el-button type="primary" @click="qrDialogVisible = false">关闭</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
	import {
		applyCmbMerchantCheckout,
		applyMerchantCheckoutCoupon,
		mockPayMerchantCheckout,
		previewMerchantFile,
		queryCmbPaymentByPaymentOrder,
		queryMerchantCheckoutDetail
	} from '@/api/mall'

	const DEFAULT_GOODS_IMAGE = require('@/assets/mall/goods/goods-demo.png')

	export default {
		name: 'MallStorePayment',
		data() {
			return {
				loading: false,
				paying: false,
				couponUpdating: false,
				qrDialogVisible: false,
				paymentStatusTimer: null,
				cmbQueryStartTimer: null,
				cmbQueryTimer: null,
				checkingPaymentStatus: false,
				queryingCmbPayment: false,
				paymentResultHandled: false,
				cmbPaymentResult: {
					payOrderNo: '',
					qrCodeImage: '',
					payableAmount: 0,
					qrExpireTime: ''
				},
				payOrderId: '',
				payStatus: 0,
				remainingSeconds: 0,
				countdownTimer: null,
				expirySynced: false,
				serverPayableAmount: null,
				serverDiscountAmount: 0,
				paymentSucceeded: false,
				paymentResult: {
					orderCode: '',
					orderCount: 0,
					orders: [],
					goodsAmount: 0,
					payableAmount: 0
				},
				orderRemark: '',
				selectedCouponId: '',
				couponEnabled: false,
				payMethod: 'cmb-aggregate',
				orderInfo: {
					orderNo: '待创建',
					merchantGroups: []
				},
				couponList: [],
				paymentMethods: [
					// {
					// 	value: 'wechat',
					// 	label: '微信支付',
					// 	description: '使用微信扫码完成支付',
					// 	image: require('@/assets/memberBenefits/wx.png')
					// },
					// {
					// 	value: 'alipay',
					// 	label: '支付宝',
					// 	description: '使用支付宝扫码完成支付',
					// 	image: require('@/assets/memberBenefits/zfb.png')
					// },
					{
						value: 'bank-aggregate',
						label: '银行转账',
						description: '对公转账，1-3个工作日到账',
						image: '',
						iconClass: 'is-bank'
					},
					{
						value: 'cmb-aggregate',
						label: '聚合码支付',
						description: '支持微信、支付宝扫码支付',
						image: '',
						iconClass: 'is-cmb',
						icon: 'el-icon-full-screen'
					}
				]
			}
		},
		computed: {
			allGoods() {
				return this.orderInfo.merchantGroups.reduce((list, merchant) => list.concat(merchant.goodsList), [])
			},
			goodsQuantity() {
				return this.allGoods.reduce((total, goods) => total + Number(goods.quantity || 0), 0)
			},
			goodsTotal() {
				return this.allGoods.reduce((total, goods) => total + Number(goods.unitPrice || 0) * Number(goods.quantity || 0), 0)
			},
			selectedCoupon() {
				return this.couponList.find(coupon => coupon.id === this.selectedCouponId)
			},
			discountAmount() {
				return this.serverDiscountAmount
			},
			payableAmount() {
				return this.serverPayableAmount === null ? Math.max(this.goodsTotal - this.discountAmount, 0) : this.serverPayableAmount
			},
			paymentExpired() {
				return [2, 3].includes(Number(this.payStatus)) || this.remainingSeconds <= 0
			},
			paymentStatusMessage() {
				if (Number(this.payStatus) === 2) return '订单已取消'
				return '订单已超时关闭，请重新购买'
			},
			countdownText() {
				const seconds = Math.max(0, Number(this.remainingSeconds || 0))
				const minutes = Math.floor(seconds / 60)
				const restSeconds = seconds % 60
				return `${String(minutes).padStart(2, '0')}:${String(restSeconds).padStart(2, '0')}`
			},
			usableCouponCount() {
				return this.couponList.filter(coupon => coupon.usable && coupon.amount > 0).length
			},
			paymentMethodLabel() {
				const paymentMethod = this.paymentMethods.find(method => method.value === this.payMethod)
				return paymentMethod ? paymentMethod.label : '在线支付'
			},
			paymentOrderCodes() {
				return Array.isArray(this.paymentResult.orders) ? this.paymentResult.orders : []
			},
			paymentSuccessTip() {
				if (this.payMethod === 'bank-aggregate') {
					return '温馨提示：银行转账信息已提交，订单将依次进入财务审核和管理员审核，全部审核通过后进入发货流程。您可以在“我的订单”查看最新状态。'
				}
				return '温馨提示：支付信息已确认，订单正在等待管理员审核，审核通过后进入发货流程。您可以在“我的订单”查看最新状态。'
			}
		},
		created() {
			this.loadSettlement()
		},
		beforeDestroy() {
			this.stopCountdown()
			this.stopPaymentPolling()
		},
		methods: {
			async loadSettlement() {
				this.loading = true
				try {
					this.payOrderId = String(this.$route.query.payOrderId || '')
					if (!this.payOrderId) {
						this.$modal.msgWarning('支付单不存在，请重新结算')
						this.$router.replace('/mallStore/index')
						return
					}
					const response = await queryMerchantCheckoutDetail({ payOrderId: this.payOrderId })
					this.applyCheckoutDetail(response.data || {})
				} finally {
					this.loading = false
				}
			},
			// 支付页只展示建单时保存的订单快照，不能再按商品当前价格重新计算。
			applyCheckoutDetail(data) {
				this.payOrderId = data.payOrderId || this.payOrderId
				this.payStatus = Number(data.payStatus || 0)
				this.remainingSeconds = Number(data.remainingSeconds || 0)
				this.serverPayableAmount = Number(data.payableAmount || 0)
				this.serverDiscountAmount = Number(data.discountAmount || 0)
				this.orderInfo.orderNo = data.payOrderNo || '-'
				this.couponEnabled = Boolean(data.couponEnabled)
				this.couponList = Array.isArray(data.couponList) ? data.couponList : []
				this.selectedCouponId = String(data.selectedUserCouponId || '')
				this.orderInfo.merchantGroups = this.mapMerchantGroups(data.merchantGroups || [])
				this.paymentResult = data
				this.paymentSucceeded = this.payStatus === 1
				if (this.payStatus === 1) {
					this.stopCountdown()
				} else {
					this.startCountdown()
				}
			},
			mapMerchantGroups(groups) {
				return groups.map(group => ({
					merchantId: group.shopId || 'platform',
					merchantName: group.merchantName || '平台自营',
					selfOperated: Boolean(group.selfOperated),
					goodsList: (group.goodsList || []).map(goods => ({
						cartItemId: goods.cartItemId,
						goodsId: goods.goodsId,
						image: goods.mainImage ? previewMerchantFile() + goods.mainImage : DEFAULT_GOODS_IMAGE,
						title: goods.goodsTitle || '未命名商品',
						introduction: goods.guideTitle || '',
						category: goods.categoryName || '综合服务',
						quantity: Number(goods.quantity || 1),
						unitPrice: Number(goods.unitPrice || 0)
					}))
				}))
			},
			startCountdown() {
				this.stopCountdown()
				if (this.paymentExpired) return
				this.countdownTimer = window.setInterval(() => {
					this.remainingSeconds = Math.max(0, this.remainingSeconds - 1)
					if (this.remainingSeconds === 0) this.syncExpiredOrder()
				}, 1000)
			},
			stopCountdown() {
				if (this.countdownTimer) {
					window.clearInterval(this.countdownTimer)
					this.countdownTimer = null
				}
			},
			async syncExpiredOrder() {
				this.stopCountdown()
				if (this.expirySynced || !this.payOrderId) return
				this.expirySynced = true
				try {
					const response = await queryMerchantCheckoutDetail({ payOrderId: this.payOrderId })
					this.applyCheckoutDetail(response.data || {})
				} finally {
					this.expirySynced = false
				}
			},
			formatAmount(value) {
				return Number(value || 0).toFixed(2)
			},
			couponValuePrefix(coupon) {
				return Number(coupon.couponType) === 10 ? '¥' : ''
			},
			couponValue(coupon) {
				return Number(coupon.faceValue || 0)
			},
			couponValueSuffix(coupon) {
				if (Number(coupon.couponType) === 20) return '折'
				if (Number(coupon.couponType) === 30) return Number(coupon.measureType) === 20 ? '天' : '次'
				return ''
			},
			couponValueText(coupon) {
				return `${this.couponValuePrefix(coupon)}${this.couponValue(coupon)}${this.couponValueSuffix(coupon)}`
			},
			couponSelectLabel(coupon) {
				return `${this.couponValueText(coupon)} ${coupon.name || '优惠券'}`
			},
			async handleCouponChange(userCouponId) {
				if (this.couponUpdating || this.paymentExpired) return
				const previousCouponId = String(this.paymentResult.selectedUserCouponId || '')
				this.couponUpdating = true
				try {
					const response = await applyMerchantCheckoutCoupon({
						payOrderId: this.payOrderId,
						userCouponId
					})
					this.applyCheckoutDetail(response.data || {})
				} catch (error) {
					this.selectedCouponId = previousCouponId
					return error
				} finally {
					this.couponUpdating = false
				}
			},
			handleImageError(event) {
				event.target.src = DEFAULT_GOODS_IMAGE
			},
			// 申请当前支付单的聚合收款码并展示弹窗。
			async openCmbPayDialog() {
				if (this.paying) return
				this.paying = true
				try {
					const response = await applyCmbMerchantCheckout({
						payOrderId: this.payOrderId,
						remark: this.orderRemark
					})
					const result = response.data || {}
					this.applyCheckoutDetail(result)
					if (this.paymentSucceeded) {
						this.$modal.msgSuccess('该订单已支付成功')
						return
					}
					if (this.paymentExpired) {
						this.$modal.msgWarning(this.paymentStatusMessage)
						return
					}
					if (!result.qrCodeImage) {
						this.$modal.msgError('未获取到收款二维码，请稍后重试')
						return
					}
					this.cmbPaymentResult = result
					this.qrDialogVisible = true
					this.$nextTick(() => this.startPaymentPolling())
				} finally {
					this.paying = false
				}
			},
			// 付款码展示后轮询本地支付状态，并定时向招行主动确认。
			startPaymentPolling() {
				this.stopPaymentPolling()
				this.paymentResultHandled = false
				this.paymentStatusTimer = window.setInterval(() => this.checkCheckoutPaymentStatus(), 2000)
				this.cmbQueryStartTimer = window.setTimeout(() => {
					this.checkCmbPaymentStatus()
					this.cmbQueryTimer = window.setInterval(() => this.checkCmbPaymentStatus(), 10000)
				}, 15000)
			},
			// 查询商城支付主单，及时响应支付通知或后台补查结果。
			async checkCheckoutPaymentStatus() {
				if (this.checkingPaymentStatus || this.paymentResultHandled || !this.payOrderId) return
				this.checkingPaymentStatus = true
				try {
					const response = await queryMerchantCheckoutDetail({ payOrderId: this.payOrderId })
					const detail = response.data || {}
					if (Number(detail.payStatus) === 1) {
						this.handlePaymentSuccess(detail)
					} else if ([2, 3].includes(Number(detail.payStatus)) || Number(detail.remainingSeconds || 0) <= 0) {
						this.handlePaymentStopped(detail, Number(detail.payStatus) === 2 ? '订单已取消' : '订单已超时关闭')
					}
				} catch (error) {
					// 临时查询异常时保留付款码，等待下一轮查询。
				} finally {
					this.checkingPaymentStatus = false
				}
			},
			// 调用招行支付结果查询，通知延迟时主动确认支付结果。
			async checkCmbPaymentStatus() {
				if (this.queryingCmbPayment || this.paymentResultHandled || !this.payOrderId) return
				this.queryingCmbPayment = true
				try {
					const response = await queryCmbPaymentByPaymentOrder({ payOrderId: this.payOrderId })
					const result = response.data || {}
					if (result.tradeState === 'S' && result.syncStatus === 'PAID') {
						await this.checkCheckoutPaymentStatus()
						return
					}
					if (['C', 'D', 'F'].includes(result.tradeState)) {
						this.handlePaymentStopped(null, '支付未完成，当前付款码已失效')
					} else if (result.tradeState === 'R') {
						this.handlePaymentStopped(null, '该笔支付已进入退款处理')
					}
				} catch (error) {
					// 招行暂时不可用时不关闭弹窗，等待下一轮主动查询。
				} finally {
					this.queryingCmbPayment = false
				}
			},
			// 支付成功后关闭弹窗并刷新支付页状态。
			handlePaymentSuccess(detail) {
				if (this.paymentResultHandled) return
				this.paymentResultHandled = true
				this.stopPaymentPolling()
				this.applyCheckoutDetail(detail)
				this.qrDialogVisible = false
				this.$modal.msgSuccess('支付成功')
				this.$nextTick(() => {
					const page = document.querySelector('.mall-payment-page')
					if (page) page.scrollTop = 0
				})
			},
			// 支付终止或订单关闭后停止查询并关闭弹窗。
			handlePaymentStopped(detail, message) {
				if (this.paymentResultHandled) return
				this.paymentResultHandled = true
				this.stopPaymentPolling()
				if (detail) this.applyCheckoutDetail(detail)
				this.qrDialogVisible = false
				this.$modal.msgWarning(message)
			},
			// 用户主动关闭付款码弹窗后停止页面轮询。
			handleQrDialogClosed() {
				this.stopPaymentPolling()
			},
			// 清除付款码相关的本地和招行查询定时器。
			stopPaymentPolling() {
				if (this.paymentStatusTimer) window.clearInterval(this.paymentStatusTimer)
				if (this.cmbQueryStartTimer) window.clearTimeout(this.cmbQueryStartTimer)
				if (this.cmbQueryTimer) window.clearInterval(this.cmbQueryTimer)
				this.paymentStatusTimer = null
				this.cmbQueryStartTimer = null
				this.cmbQueryTimer = null
			},
			// 当前阶段确认后调用模拟支付接口；以后只需替换接口为真实收银台结果确认。
			async handlePay() {
				if (this.paymentExpired) {
					this.$modal.msgWarning(this.paymentStatusMessage)
					return
				}
				if (!this.allGoods.length) {
					this.$modal.msgWarning('没有可结算的商品')
					return
				}
				if (!this.payMethod) {
					this.$modal.msgWarning('请选择支付方式')
					return
				}
				const confirmed = await this.$modal
					.confirm(`确认支付 ¥ ${this.formatAmount(this.payableAmount)} 吗？`)
					.then(() => true)
					.catch(() => false)
				if (!confirmed) {
					return
				}
				if (this.payMethod === 'cmb-aggregate') {
					await this.openCmbPayDialog()
					return
				}
				this.paying = true
				try {
					const response = await mockPayMerchantCheckout({
						payOrderId: this.payOrderId,
						paymentType: this.payMethod,
						remark: this.orderRemark
					})
					this.applyCheckoutDetail(response.data || {})
					if (!this.paymentSucceeded) {
						this.$modal.msgWarning(this.paymentStatusMessage)
						return
					}
					this.$nextTick(() => {
						const page = document.querySelector('.mall-payment-page')
						if (page) page.scrollTop = 0
					})
				} finally {
					this.paying = false
				}
			}
		}
	}
</script>

<style lang="scss">
	.cmb-pay-dialog {
		overflow: hidden;
		border-radius: 20px;
		box-shadow: 0 22px 60px rgba(35, 69, 104, 0.2);

		.el-dialog__header {
			padding: 22px 24px 18px;
			text-align: center;
			background: linear-gradient(135deg, #f4f9ff 0%, #eef6ff 100%);
		}

		.el-dialog__title {
			color: #223b58;
			font-size: 20px;
			font-weight: 600;
		}

		.el-dialog__headerbtn {
			top: 23px;
			right: 24px;
		}

		.el-dialog__body {
			padding: 24px 34px 18px;
		}

		.el-dialog__footer {
			padding: 12px 34px 22px;
			text-align: center;
		}

		.el-dialog__footer .el-button {
			min-width: 112px;
			border-radius: 18px;
		}
	}
</style>

<style lang="scss" scoped>
	.mall-payment-page {
		min-height: 760px;
		padding: 22px 0 60px;
		color: #263548;
		background: #f4f7fb;
	}

	.cmb-qrcode-content {
		padding: 18px 16px 14px;
		border: 1px solid #e8f1fa;
		border-radius: 16px;
		text-align: center;
		background: linear-gradient(180deg, #fbfdff 0%, #f6faff 100%);
	}

	.cmb-qrcode-order,
	.cmb-qrcode-expire {
		margin: 0;
		color: #667085;
		font-size: 14px;
	}

	.cmb-qrcode-image {
		display: block;
		box-sizing: border-box;
		width: 252px;
		height: 252px;
		margin: 18px auto;
		padding: 8px;
		border: 1px solid #ebeef5;
		border-radius: 14px;
		background: #ffffff;
		box-shadow: 0 10px 26px rgba(49, 91, 133, 0.1);
	}

	.cmb-qrcode-amount {
		margin: 10px 0;
		color: #e3262e;
		font-size: 18px;
		font-weight: 600;
	}

	.cmb-qrcode-tip {
		margin: 8px 0 10px;
		color: #303133;
	}

	.payment-container {
		width: $main-page-width;
		margin: 0 auto;
	}

	.payment-success-card {
		min-height: 760px;
		padding: 95px 110px 70px;
		border: 1px solid #e2e9f1;
		border-radius: 12px;
		text-align: center;
		background: #ffffff;
		box-shadow: 0 10px 32px rgba(48, 76, 110, 0.07);

		.success-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 112px;
			height: 112px;
			margin: 0 auto 24px;
			border-radius: 16px;
			color: #ffffff;
			font-size: 68px;
			background: #43cb87;
			box-shadow: 0 12px 24px rgba(47, 184, 119, 0.23);
		}

		h1 {
			margin: 0;
			font-size: 30px;
		}

		.success-order-number {
			margin: 18px 0 0;
			color: #566274;
			font-size: 16px;
		}

		.success-order-list {
			display: grid;
			gap: 6px;
			max-width: 560px;
			margin: 12px auto 0;
			color: #566274;
			font-size: 14px;
		}

		.success-amount {
			margin: 30px 0 44px;
			color: #e3262e;
			font-size: 38px;
			font-weight: 700;

			small {
				margin-right: 8px;
				font-size: 24px;
			}
		}

		.success-summary {
			padding: 18px 28px;
			text-align: left;
			background: #f2f7fa;

			> div {
				display: flex;
				align-items: center;
				justify-content: space-between;
				min-height: 44px;
				font-size: 15px;
			}

			.success-paid-row {
				margin-top: 5px;
				padding-top: 10px;
				border-top: 1px solid #cfd9df;

				strong {
					color: #e3262e;
				}
			}
		}

		.success-tip {
			margin: 30px auto 0;
			color: #4e5b6c;
			font-size: 15px;
			line-height: 26px;
			text-align: left;
		}

		.success-actions {
			display: flex;
			justify-content: center;
			gap: 16px;
			margin-top: 32px;

			.el-button {
				min-width: 128px;
				height: 42px;
			}
		}
	}

	.breadcrumb-wrap {
		padding: 2px 0 20px;

		::v-deep .el-breadcrumb__inner {
			font-size: 14px;
		}
	}

	.payment-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 22px;

		> div {
			display: flex;
			align-items: center;
		}

		.heading-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 52px;
			height: 52px;
			margin-right: 14px;
			border-radius: 14px;
			color: #ffffff;
			font-size: 25px;
			background: linear-gradient(145deg, #1875cf, #0e5daa);
			box-shadow: 0 8px 18px rgba(19, 104, 187, 0.22);
		}

		h1 {
			margin: 0;
			font-size: 25px;
		}

		p {
			margin: 6px 0 0;
			color: #7d8998;
			font-size: 14px;
		}
	}

	.secure-tip {
		padding: 9px 14px;
		border-radius: 18px;
		color: #39815e;
		font-size: 14px;
		background: #e8f6ef;

		i {
			margin-right: 5px;
		}
	}

	.payment-layout {
		display: grid;
		align-items: start;
		grid-template-columns: minmax(0, 1fr) 405px;
		gap: 22px;
	}

	.order-card,
	.remark-card,
	.settlement-card {
		overflow: hidden;
		border: 1px solid #e2e9f1;
		border-radius: 12px;
		background: #ffffff;
		box-shadow: 0 8px 28px rgba(48, 76, 110, 0.06);
	}

	.card-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 22px 24px;
		border-bottom: 1px solid #e7edf3;

		> div {
			display: flex;
			align-items: center;
		}

		.heading-mark {
			width: 4px;
			height: 20px;
			margin-right: 10px;
			border-radius: 2px;
			background: #1873ca;
		}

		h2 {
			margin: 0;
			font-size: 19px;
		}

		.order-number {
			color: #8a95a3;
			font-size: 14px;
		}

		.order-number-wrap {
			display: flex;
			align-items: flex-end;
			flex-direction: column;
			gap: 7px;
		}

		.payment-countdown {
			color: #d97816;
			font-size: 14px;
			font-weight: 600;

			&.expired {
				color: #909399;
			}
		}
	}

	.goods-table-heading,
	.order-goods-row {
		display: grid;
		align-items: center;
		grid-template-columns: minmax(0, 1fr) 115px 80px 120px;
	}

	.goods-table-heading {
		padding: 12px 24px;
		color: #7c8794;
		font-size: 14px;
		text-align: center;
		background: #f7f9fc;

		span:first-child {
			text-align: left;
		}
	}

	.merchant-group {
		& + & {
			border-top: 8px solid #f4f7fa;
		}
	}

	.merchant-heading {
		display: flex;
		align-items: center;
		height: 52px;
		padding: 0 24px;
		border-top: 1px solid #e8edf3;
		border-bottom: 1px solid #e8edf3;
		background: #fbfcfe;

		.merchant-logo {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 30px;
			height: 30px;
			margin-right: 9px;
			border-radius: 8px;
			color: #1c73c5;
			font-size: 17px;
			background: #e8f2fc;
		}

		strong {
			font-size: 15px;
		}

		.self-tag {
			margin-left: 9px;
			padding: 3px 7px;
			border-radius: 4px;
			color: #ffffff;
			font-size: 14px;
			background: #1b75ca;
		}

		.merchant-service {
			margin-left: auto;
			color: #66809c;
			font-size: 14px;
			cursor: pointer;

			i {
				margin-right: 5px;
			}
		}
	}

	.order-goods-row {
		min-height: 142px;
		padding: 20px 24px;

		& + & {
			border-top: 1px dashed #dfe5ec;
		}
	}

	.goods-info {
		display: flex;
		align-items: center;
		min-width: 0;
		padding-right: 20px;
	}

	.goods-image {
		flex: 0 0 104px;
		height: 92px;
		overflow: hidden;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #f1f4f8;

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.goods-copy {
		min-width: 0;
		margin-left: 14px;

		h3 {
			margin: 0 0 7px;
			overflow: hidden;
			font-size: 16px;
			white-space: nowrap;
			text-overflow: ellipsis;
		}

		p {
			display: -webkit-box;
			margin: 0 0 8px;
			overflow: hidden;
			color: #7b8795;
			font-size: 14px;
			line-height: 21px;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
		}

		span {
			color: #52779d;
			font-size: 14px;
		}
	}

	.unit-price,
	.goods-quantity,
	.subtotal {
		font-size: 15px;
		text-align: center;
	}

	.unit-price,
	.subtotal {
		small {
			margin-right: 2px;
			font-size: 14px;
		}
	}

	.goods-quantity {
		color: #6f7b89;
	}

	.subtotal {
		color: #e76122;
		font-weight: 600;
	}

	.remark-card {
		margin-top: 22px;
		padding: 22px 24px 25px;
	}

	.remark-heading {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		margin-bottom: 15px;

		h2 {
			margin: 0;
			font-size: 18px;
		}

		p,
		> span {
			color: #8b96a4;
			font-size: 14px;
		}

		p {
			margin: 5px 0 0;
		}
	}

	.remark-card ::v-deep .el-textarea__inner {
		padding: 13px 15px;
		border-color: #dce3eb;
		font-size: 14px;
		line-height: 22px;
		resize: none;
	}

	.settlement-column {
		position: sticky;
		top: 18px;
	}

	.settlement-card {
		padding: 23px;
	}

	.settlement-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 18px;
		border-bottom: 1px solid #e7ecf2;

		h2 {
			margin: 0;
			font-size: 20px;
		}

		span {
			color: #8792a0;
			font-size: 14px;
		}
	}

	.amount-list {
		padding: 17px 0 10px;

		> div {
			display: flex;
			align-items: center;
			justify-content: space-between;
			min-height: 34px;
			font-size: 14px;
		}

		span {
			color: #697687;
		}

		strong {
			font-weight: 500;
		}

		.discount-row strong {
			color: #e16025;
		}
	}

	.payable-amount {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 17px 0;
		border-top: 1px dashed #dbe2ea;
		border-bottom: 1px solid #e5ebf1;

		> span {
			font-size: 15px;
			font-weight: 600;
		}

		> div {
			color: #eb6020;

			small {
				margin-right: 3px;
				font-size: 16px;
			}

			strong {
				font-size: 28px;
			}
		}
	}

	.settlement-section {
		padding-top: 20px;
	}

	.section-title {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 12px;
		font-size: 15px;
		font-weight: 600;

		i {
			margin-right: 6px;
			color: #2578c7;
		}

		> span {
			color: #dc6a27;
			font-size: 14px;
			font-weight: 400;
		}
	}

	.payment-method-list {
		display: grid;
		gap: 9px;
	}

	.coupon-select-wrap,
	.coupon-select {
		width: 100%;
	}

	::v-deep .coupon-select {
		.el-input__inner {
			height: 40px;
			padding: 0 34px 0 12px;
			border-color: #dfe5ec;
			border-radius: 6px;
			color: #354354;
			font-size: 14px;
		}

		.el-input__suffix {
			display: flex;
			align-items: center;
		}

		&:hover .el-input__inner,
		.el-input.is-focus .el-input__inner {
			border-color: #2479cb;
		}
	}

	.coupon-selected-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		margin-top: 7px;
		padding: 0 2px;
		color: #778493;
		font-size: 12px;
		line-height: 18px;

		span {
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		i {
			margin-right: 5px;
			color: #2d9d68;
		}

		strong {
			flex: none;
			color: #df5c25;
			font-size: 13px;
		}
	}

	.coupon-empty {
		display: flex;
		align-items: center;
		min-height: 54px;
		padding: 10px 12px;
		border: 1px dashed #dbe3eb;
		border-radius: 8px;
		color: #8b97a5;
		font-size: 14px;
		background: #fafbfd;

		i {
			margin-right: 9px;
			color: #91a8be;
			font-size: 19px;
		}
	}

	.payment-method {
		position: relative;
		display: flex;
		align-items: center;
		min-height: 62px;
		padding: 10px 12px;
		overflow: hidden;
		border: 1px solid #dfe5ec;
		border-radius: 8px;
		background: #ffffff;
		cursor: pointer;
		transition: border-color 0.2s ease, background 0.2s ease;

		> input {
			position: absolute;
			opacity: 0;
			pointer-events: none;
		}

		&.active {
			border-color: #2479cb;
			background: #f3f8fd;

			.method-check {
				display: block;
			}
		}
	}

	.payment-method > div {
		min-width: 0;

		strong,
		span {
			display: block;
		}

		strong {
			font-size: 14px;
		}

		span {
			margin-top: 4px;
			color: #8994a2;
			font-size: 14px;
		}
	}

	.method-check {
		position: absolute;
		right: 0;
		bottom: 0;
		display: none;
		width: 23px;
		height: 23px;
		padding: 7px 0 0 7px;
		border-radius: 12px 0 0;
		color: #ffffff;
		font-size: 12px;
		background: #2479cb;
	}

	.payment-method {
		min-height: 58px;

		img,
		.payment-icon {
			flex: 0 0 38px;
			width: 38px;
			height: 38px;
			margin-right: 11px;
			border-radius: 8px;
			object-fit: cover;
		}
	}

	.payment-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		color: #ffffff;
		font-size: 21px;

		&.is-bank {
			background: linear-gradient(145deg, #f0a322, #e87c1f);
		}

		&.is-cmb {
			background: linear-gradient(145deg, #28a96b, #1b82d1);
		}
	}

	.pay-button {
		width: 100%;
		height: 50px;
		margin-top: 24px;
		border: 0;
		border-radius: 7px;
		font-size: 17px;
		background: linear-gradient(90deg, #1672ca, #2889df);
		box-shadow: 0 9px 20px rgba(27, 116, 201, 0.2);
	}

	::v-deep .settlement-card .el-alert {
		margin-top: 20px;
	}

	.agreement-tip {
		margin: 13px 0 0;
		color: #929ca8;
		font-size: 14px;
		line-height: 21px;
		text-align: center;

		i {
			margin-right: 4px;
			color: #49a978;
		}
	}

	@media (max-width: 1400px) {
		.payment-container {
			width: calc(100% - 48px);
		}

		.payment-layout {
			grid-template-columns: minmax(0, 1fr) 370px;
		}
	}

	@media (max-width: 1100px) {
		.payment-layout {
			grid-template-columns: 1fr;
		}

		.payment-success-card {
			padding-right: 40px;
			padding-left: 40px;
		}

		.settlement-column {
			position: static;
		}
	}
</style>

<style lang="scss">
	.checkout-coupon-popper {
		.el-select-dropdown__list {
			padding: 5px 0;
		}

		.el-select-dropdown__item {
			height: auto;
			min-height: 48px;
			padding: 0 7px;
			line-height: normal;

			&.hover,
			&:hover {
				background: #f4f7fa;
			}

			&.selected {
				color: inherit;
				background: #eef6fd;
			}

			&.is-disabled {
				cursor: not-allowed;
				opacity: 0.62;
			}
		}

		.coupon-dropdown-row {
			display: flex;
			align-items: center;
			gap: 9px;
			min-width: 320px;
			max-width: 430px;
			min-height: 48px;
			padding: 5px 6px;
		}

		.coupon-dropdown-value {
			flex: 0 0 58px;
			color: #df5c25;
			font-size: 15px;
			font-weight: 700;
			text-align: center;
		}

		.coupon-dropdown-none .coupon-dropdown-value {
			color: #667587;
			font-size: 13px;
		}

		.coupon-dropdown-copy {
			min-width: 0;
			flex: 1;

			strong,
			small {
				display: block;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			strong {
				color: #354354;
				font-size: 13px;
				line-height: 19px;
			}

			small {
				color: #8994a2;
				font-size: 12px;
				line-height: 17px;
			}
		}

		.coupon-dropdown-saving,
		.coupon-dropdown-status {
			flex: none;
			font-size: 12px;
		}

		.coupon-dropdown-saving {
			color: #df5c25;
			font-weight: 600;
		}

		.coupon-dropdown-status {
			color: #98a2ad;
		}
	}
</style>
